import React from 'react';
import NavBar from '../components/NavBar';
import Footer from '../components/Footer';

const PrivacyPolicyPage = () => {
    return (
        <div className="flex flex-col min-h-screen bg-white text-gray-800">
            <NavBar />
            <main className="flex-1">
                <header className="bg-gray-50 border-b border-gray-200">
                    <div className="max-w-5xl mx-auto px-4 py-10">
                        <h1 className="text-3xl font-bold text-crumbblue">Privacy Policy</h1>
                        <p className="text-sm text-gray-600 mt-2">Effective Date: August 11, 2025</p>
                        <p className="text-sm text-gray-600">Service Name: CrumbCRM</p>
                    </div>
                </header>

                <div className="max-w-5xl mx-auto px-4 py-10 space-y-10 leading-7">
                    <section>
                        <h2 className="text-xl font-semibold text-crumbblue">1) Scope</h2>
                        <p className="mt-2">
                            This Privacy Policy explains how CrumbCRM (“we,” “us,” or “our”) collects, uses, shares, and protects
                            information when you use our website, application, and related services (the “Services”).
                            By using the Services, you agree to this Policy.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-xl font-semibold text-crumbblue">2) Information We Collect</h2>
                        <ul className="list-disc pl-6 mt-2 space-y-2">
                            <li>
                                <span className="font-medium">Account & Profile Data:</span> name, email address, password hash, and related settings you provide when creating an account.
                            </li>
                            <li>
                                <span className="font-medium">Billing & Payments:</span> billing name and email, and limited payment details (e.g., card brand, last 4 digits). We do not store full card numbers. Payments are processed by Stripe.
                            </li>
                            <li>
                                <span className="font-medium">Content You Provide:</span> client records, interactions, notes, tags, and uploaded files that you store in CrumbCRM.
                            </li>
                            <li>
                                <span className="font-medium">Technical & Usage Data:</span> IP address, device and browser information, pages viewed, app events, and timestamps; approximate location based on IP; cookies and similar technologies.
                            </li>
                            <li>
                                <span className="font-medium">Support/Feedback:</span> messages and attachments you send when requesting help or providing feedback.
                            </li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="text-xl font-semibold text-crumbblue">3) How We Use Information</h2>
                        <ul className="list-disc pl-6 mt-2 space-y-2">
                            <li>Provide, operate, secure, and improve the Services (including authentication and performance).</li>
                            <li>Process payments and manage subscriptions (via Stripe).</li>
                            <li>Personalize features, remember preferences, and measure usage.</li>
                            <li>Communicate about service updates, security alerts, and billing notices.</li>
                            <li>Detect, investigate, and prevent fraud, abuse, and security incidents.</li>
                            <li>Comply with legal obligations and enforce our terms.</li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="text-xl font-semibold text-crumbblue">4) Legal Bases (EU/UK)</h2>
                        <p className="mt-2">
                            Where GDPR/UK GDPR applies, we process personal data based on: contract (to provide the Services),
                            legitimate interests (security, improvement, preventing abuse), consent (where required, e.g., certain cookies),
                            and legal obligations.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-xl font-semibold text-crumbblue">5) Sharing & Disclosure</h2>
                        <p className="mt-2">
                            We do <span className="font-semibold">not sell</span> your personal data and do not share it with third parties for their own marketing.
                            We may share data with:
                        </p>
                        <ul className="list-disc pl-6 mt-2 space-y-2">
                            <li>
                                <span className="font-medium">Service Providers/Processors:</span> including Stripe (payments), hosting, email delivery, and support tools—only as needed to provide the Services and subject to confidentiality and data protection obligations.
                            </li>
                            <li>
                                <span className="font-medium">Legal & Safety:</span> if required by law or to protect rights, safety, and security.
                            </li>
                            <li>
                                <span className="font-medium">Business Transfers:</span> in connection with a merger, acquisition, or sale of assets, with continued protections consistent with this Policy.
                            </li>
                        </ul>
                        <div className="mt-2 p-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-700">
                            <span className="font-medium">Stripe (Payments):</span> Your payment information is transmitted directly to Stripe for processing.
                            CrumbCRM does not store full card numbers or CVV.
                        </div>
                    </section>

                    <section>
                        <h2 className="text-xl font-semibold text-crumbblue">6) Cookies & Similar Technologies</h2>
                        <p className="mt-2">
                            We use cookies and similar technologies to keep you signed in, remember preferences, improve performance,
                            and measure usage in aggregate. You can manage cookies via your browser settings. Blocking essential cookies
                            may impact the functionality of the Services.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-xl font-semibold text-crumbblue">7) Data Retention</h2>
                        <p className="mt-2">
                            We retain personal data for as long as necessary to provide the Services, comply with legal obligations,
                            resolve disputes, and enforce agreements. When no longer needed, we aim to delete or anonymize data within
                            reasonable timeframes. You may request deletion of your account data (see Your Rights).
                        </p>
                    </section>

                    <section>
                        <h2 className="text-xl font-semibold text-crumbblue">8) Security</h2>
                        <p className="mt-2">
                            We implement reasonable technical and organizational measures (e.g., encryption in transit, access controls)
                            to protect personal data. No method of transmission or storage is 100% secure; we continuously improve safeguards.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-xl font-semibold text-crumbblue">9) Your Rights & Choices</h2>
                        <p className="mt-2">
                            Depending on your region, you may have rights to access, correct, update, or delete personal data; request data
                            portability; object to or restrict certain processing; and withdraw consent where processing is based on consent.
                            We will verify your identity and respond within applicable timeframes.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-xl font-semibold text-crumbblue">10) U.S. State Disclosures (e.g., California)</h2>
                        <p className="mt-2">
                            We do not “sell” or “share” personal information as defined by the CCPA/CPRA.
                            California residents may exercise applicable rights (access, deletion, correction, portability, and opt‑out of
                            cross‑context behavioral advertising, if implemented).
                        </p>
                    </section>

                    <section>
                        <h2 className="text-xl font-semibold text-crumbblue">11) Children’s Privacy</h2>
                        <p className="mt-2">
                            The Services are not directed to children under 13 (or 16 where applicable). We do not knowingly collect
                            personal data from children. If you believe a child has provided personal data, let us know so we can remove it.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-xl font-semibold text-crumbblue">12) International Data Transfers</h2>
                        <p className="mt-2">
                            We may process and store information in the United States and other countries. Where required, we use appropriate
                            safeguards (e.g., Standard Contractual Clauses) for cross‑border transfers.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-xl font-semibold text-crumbblue">13) Third‑Party Links</h2>
                        <p className="mt-2">
                            The Services may link to third‑party sites. Their privacy practices are governed by their own policies.
                            Review those policies before providing information.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-xl font-semibold text-crumbblue">14) Changes to This Policy</h2>
                        <p className="mt-2">
                            We may update this Policy from time to time. We will post the updated version with a new effective date,
                            and if changes are material, we will provide additional notice.
                        </p>
                    </section>
                </div>
            </main>
            <Footer />
        </div>
    );
};

export default PrivacyPolicyPage;